import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * AM/PM routine notes — free-text per side, persisted per user.
 * Lives under the AM/PM protocol blocks on /protocol.
 */
export default function RoutineNotes() {
  const [am, setAm] = useState("");
  const [pm, setPm] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [savingAm, setSavingAm] = useState(false);
  const [savingPm, setSavingPm] = useState(false);
  const [savedAm, setSavedAm] = useState(false);
  const [savedPm, setSavedPm] = useState(false);

  useEffect(() => {
    api.get("/notes").then((r) => {
      setAm(r.data?.am || "");
      setPm(r.data?.pm || "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (which) => {
    const setSaving = which === "am" ? setSavingAm : setSavingPm;
    const setSaved = which === "am" ? setSavedAm : setSavedPm;
    setSaving(true); setSaved(false);
    try {
      await api.put("/notes", { am, pm });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="mt-12" data-testid="routine-notes">
      <h2 className="font-display text-2xl mb-5">Your notes</h2>
      <p className="text-sm text-[var(--aa-text-2)] mb-5 max-w-2xl">A private space to track how your skin is responding, products you're trying, or anything you want to remember about your routine. Auto-saved when you click Save.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <NoteCard
          title="Morning notes"
          tint="aa-card-warm"
          value={am}
          onChange={setAm}
          onSave={() => save("am")}
          saving={savingAm}
          saved={savedAm}
          testid="notes-am"
        />
        <NoteCard
          title="Evening notes"
          tint="aa-card-stone"
          value={pm}
          onChange={setPm}
          onSave={() => save("pm")}
          saving={savingPm}
          saved={savedPm}
          testid="notes-pm"
        />
      </div>
    </div>
  );
}

function NoteCard({ title, tint, value, onChange, onSave, saving, saved, testid }) {
  return (
    <div className={`aa-card ${tint} p-6`} data-testid={`${testid}-card`}>
      <div className="aa-eyebrow mb-3">{title}</div>
      <textarea
        data-testid={`${testid}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        maxLength={4000}
        placeholder="e.g. Started new vitamin C on the 3rd — slight tingling first night, fine by morning…"
        className="w-full bg-[var(--aa-card)] border border-[var(--aa-elevated)] rounded-md p-3 text-sm text-[var(--aa-text)] focus:outline-none focus:border-[var(--aa-text)] resize-y leading-relaxed"
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          data-testid={`${testid}-save`}
          onClick={onSave}
          disabled={saving}
          className="aa-cta-secondary disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span className="aa-eyebrow text-[var(--aa-sage)]" data-testid={`${testid}-saved`}>Saved ✓</span>}
        <span className="ml-auto text-xs text-[var(--aa-text-3)]">{value.length}/4000</span>
      </div>
    </div>
  );
}
